import { useReducer, useCallback } from 'react';

// ---------------------------------------------------------------
// Screens / states
// ---------------------------------------------------------------
const SCREENS = {
  IDLE: 'idle',
  IDENTIFY: 'identify',
  CONFIRM: 'confirm',
  SELFIE: 'selfie',
  ACTION: 'action',
  SUCCESS: 'success',
  ERROR: 'error',
  ADMIN: 'admin',
};

// ---------------------------------------------------------------
// Action types
// ---------------------------------------------------------------
const ACTIONS = {
  GO_TO_IDENTIFY: 'GO_TO_IDENTIFY',
  SELECT_USER: 'SELECT_USER',
  CONFIRM_USER: 'CONFIRM_USER',
  REJECT_USER: 'REJECT_USER',
  SELFIE_COMPLETE: 'SELFIE_COMPLETE',
  CLOCK_IN_SUCCESS: 'CLOCK_IN_SUCCESS',
  CLOCK_OUT_SUCCESS: 'CLOCK_OUT_SUCCESS',
  BREAK_START_SUCCESS: 'BREAK_START_SUCCESS',
  BREAK_END_SUCCESS: 'BREAK_END_SUCCESS',
  SHOW_ERROR: 'SHOW_ERROR',
  GO_TO_IDLE: 'GO_TO_IDLE',
  GO_BACK: 'GO_BACK',
  GO_TO_ADMIN: 'GO_TO_ADMIN',
  LEAVE_ADMIN: 'LEAVE_ADMIN',
};

// ---------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------
const initialState = {
  screen: SCREENS.IDLE,
  selectedUser: null,
  clockedIn: false,
  onBreak: false,
  activeBreak: null,
  identifyMethod: null,
  successData: null,
  errorMessage: null,
  selfieData: null,
};

// ---------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------
function reducer(state, action) {
  switch (action.type) {
    // idle/error -> identify
    case ACTIONS.GO_TO_IDENTIFY: {
      if (state.screen !== SCREENS.IDLE && state.screen !== SCREENS.ERROR) return state;
      return {
        ...initialState,
        screen: SCREENS.IDENTIFY,
      };
    }

    // identify -> confirm
    case ACTIONS.SELECT_USER: {
      if (state.screen !== SCREENS.IDENTIFY) return state;
      return {
        ...state,
        screen: SCREENS.CONFIRM,
        selectedUser: action.payload.user,
        clockedIn: action.payload.clockedIn,
        onBreak: action.payload.onBreak || false,
        activeBreak: action.payload.activeBreak || null,
        identifyMethod: action.payload.method,
        errorMessage: null,
      };
    }

    // confirm -> selfie (if required & clocking in) or action
    case ACTIONS.CONFIRM_USER: {
      if (state.screen !== SCREENS.CONFIRM) return state;
      const needsSelfie = action.payload?.requireSelfie && !state.clockedIn;
      return {
        ...state,
        screen: needsSelfie ? SCREENS.SELFIE : SCREENS.ACTION,
        errorMessage: null,
      };
    }

    // selfie -> action (with captured selfie data)
    case ACTIONS.SELFIE_COMPLETE: {
      if (state.screen !== SCREENS.SELFIE) return state;
      return {
        ...state,
        screen: SCREENS.ACTION,
        selfieData: action.payload?.selfieBase64 || null,
        errorMessage: null,
      };
    }

    // confirm -> identify (user rejected / "not me")
    case ACTIONS.REJECT_USER: {
      if (state.screen !== SCREENS.CONFIRM) return state;
      return {
        ...state,
        screen: SCREENS.IDENTIFY,
        selectedUser: null,
        clockedIn: false,
        identifyMethod: null,
        errorMessage: null,
      };
    }

    // action -> success (clock in)
    case ACTIONS.CLOCK_IN_SUCCESS: {
      if (state.screen !== SCREENS.ACTION) return state;
      return {
        ...state,
        screen: SCREENS.SUCCESS,
        successData: {
          type: 'clock_in',
          ...action.payload,
        },
        errorMessage: null,
      };
    }

    // action -> success (clock out)
    case ACTIONS.CLOCK_OUT_SUCCESS: {
      if (state.screen !== SCREENS.ACTION) return state;
      return {
        ...state,
        screen: SCREENS.SUCCESS,
        successData: {
          type: 'clock_out',
          ...action.payload,
        },
        errorMessage: null,
      };
    }

    // action -> success (break start)
    case ACTIONS.BREAK_START_SUCCESS: {
      if (state.screen !== SCREENS.ACTION) return state;
      return {
        ...state,
        screen: SCREENS.SUCCESS,
        onBreak: true,
        successData: {
          type: 'break_start',
          ...action.payload,
        },
        errorMessage: null,
      };
    }

    // action -> success (break end)
    case ACTIONS.BREAK_END_SUCCESS: {
      if (state.screen !== SCREENS.ACTION) return state;
      return {
        ...state,
        screen: SCREENS.SUCCESS,
        onBreak: false,
        activeBreak: null,
        successData: {
          type: 'break_end',
          ...action.payload,
        },
        errorMessage: null,
      };
    }

    // any -> error
    case ACTIONS.SHOW_ERROR: {
      return {
        ...state,
        screen: SCREENS.ERROR,
        errorMessage: action.payload.message,
      };
    }

    // idle -> admin
    case ACTIONS.GO_TO_ADMIN: {
      if (state.screen !== SCREENS.IDLE) return state;
      return {
        ...state,
        screen: SCREENS.ADMIN,
      };
    }

    // admin -> idle
    case ACTIONS.LEAVE_ADMIN: {
      if (state.screen !== SCREENS.ADMIN) return state;
      return { ...initialState };
    }

    // any -> idle
    case ACTIONS.GO_TO_IDLE: {
      return { ...initialState };
    }

    // context-aware back navigation
    case ACTIONS.GO_BACK: {
      switch (state.screen) {
        case SCREENS.IDENTIFY:
          return { ...initialState };
        case SCREENS.CONFIRM:
          return {
            ...state,
            screen: SCREENS.IDENTIFY,
            selectedUser: null,
            clockedIn: false,
            identifyMethod: null,
            errorMessage: null,
          };
        case SCREENS.SELFIE:
          return {
            ...state,
            screen: SCREENS.CONFIRM,
            selfieData: null,
            errorMessage: null,
          };
        case SCREENS.ACTION:
          return {
            ...state,
            screen: state.selfieData ? SCREENS.SELFIE : SCREENS.CONFIRM,
            selfieData: null,
            errorMessage: null,
          };
        case SCREENS.ADMIN:
          return { ...initialState };
        case SCREENS.SUCCESS:
        case SCREENS.ERROR:
          return { ...initialState };
        default:
          return state;
      }
    }

    default:
      return state;
  }
}

/**
 * Kiosk flow state machine.
 *
 * Manages screen transitions for the kiosk clock-in / clock-out flow
 * using useReducer. Each dispatched action validates that the
 * transition is legal before updating state.
 */
export function useKioskState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  // ---------------------------------------------------------------
  // Transition helpers (all wrapped in useCallback for stability)
  // ---------------------------------------------------------------
  const goToIdentify = useCallback(() => {
    dispatch({ type: ACTIONS.GO_TO_IDENTIFY });
  }, []);

  const selectUser = useCallback((user, clockedIn, method, onBreak = false, activeBreak = null) => {
    dispatch({
      type: ACTIONS.SELECT_USER,
      payload: { user, clockedIn, method, onBreak, activeBreak },
    });
  }, []);

  const confirmUser = useCallback((requireSelfie = false) => {
    dispatch({ type: ACTIONS.CONFIRM_USER, payload: { requireSelfie } });
  }, []);

  const selfieComplete = useCallback((selfieBase64 = null) => {
    console.log('[KioskState] selfieComplete called:', { hasSelfie: !!selfieBase64, selfieLength: selfieBase64?.length || 0 });
    dispatch({ type: ACTIONS.SELFIE_COMPLETE, payload: { selfieBase64 } });
  }, []);

  const rejectUser = useCallback(() => {
    dispatch({ type: ACTIONS.REJECT_USER });
  }, []);

  const clockInSuccess = useCallback((data) => {
    dispatch({
      type: ACTIONS.CLOCK_IN_SUCCESS,
      payload: data,
    });
  }, []);

  const clockOutSuccess = useCallback((data) => {
    dispatch({
      type: ACTIONS.CLOCK_OUT_SUCCESS,
      payload: data,
    });
  }, []);

  const breakStartSuccess = useCallback((data) => {
    dispatch({
      type: ACTIONS.BREAK_START_SUCCESS,
      payload: data,
    });
  }, []);

  const breakEndSuccess = useCallback((data) => {
    dispatch({
      type: ACTIONS.BREAK_END_SUCCESS,
      payload: data,
    });
  }, []);

  const showError = useCallback((message) => {
    dispatch({
      type: ACTIONS.SHOW_ERROR,
      payload: { message },
    });
  }, []);

  const goToIdle = useCallback(() => {
    dispatch({ type: ACTIONS.GO_TO_IDLE });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: ACTIONS.GO_BACK });
  }, []);

  const goToAdmin = useCallback(() => {
    dispatch({ type: ACTIONS.GO_TO_ADMIN });
  }, []);

  const leaveAdmin = useCallback(() => {
    dispatch({ type: ACTIONS.LEAVE_ADMIN });
  }, []);

  return {
    // State
    screen: state.screen,
    selectedUser: state.selectedUser,
    clockedIn: state.clockedIn,
    onBreak: state.onBreak,
    activeBreak: state.activeBreak,
    identifyMethod: state.identifyMethod,
    successData: state.successData,
    errorMessage: state.errorMessage,
    selfieData: state.selfieData,

    // Actions
    goToIdentify,
    selectUser,
    confirmUser,
    selfieComplete,
    rejectUser,
    clockInSuccess,
    clockOutSuccess,
    breakStartSuccess,
    breakEndSuccess,
    showError,
    goToIdle,
    goBack,
    goToAdmin,
    leaveAdmin,
  };
}
